/**
 * AiRulesetTab.tsx
 *
 * WAF "AI Ruleset" inner tab — browse/filter the ML-scored rule catalog,
 * toggle individual rules or whole categories on/off, and set the ruleset mode.
 * Receives AiCfg state + setter via props; save/cancel is handled by WafTab.
 */
import { useMemo, useState } from 'react';
import { Search } from 'lucide-react';
import { SettingRow, Toolbar } from '@vnetwork/ui/layout';
import {
  Card, CardHeader, CardTitle, CardDescription, CardBody,
  Input, Switch, Badge, Skeleton, SegmentedControl,
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@vnetwork/ui/primitives';
import { DataTable, Stat, StatGroup, Pagination, EmptyState, type ColumnDef } from '@vnetwork/ui/data';
import { useWafAiCatalog, type WafAiCatalogRule } from '@/api/manticore';
import { toneColor } from '../../../shared';
import type { AiCfg } from './waf-types';

// Curated slug → friendly-label list; the live catalog may introduce extras.
const AI_CATEGORIES: [string, string][] = [
  ['sqli', 'SQL Injection'], ['xss', 'XSS'], ['rce', 'RCE'], ['lfi', 'LFI'], ['ssrf', 'SSRF'], ['scanner', 'Scanner'],
  ['auth', 'Auth'], ['disclosure', 'Disclosure'], ['protocol', 'Protocol'], ['deserialization', 'Deserialization'],
  ['upload', 'Upload'], ['log4shell', 'Log4Shell'], ['xxe', 'XXE'], ['custom', 'Custom'], ['rfi', 'RFI'], ['graphql', 'GraphQL'], ['shellshock', 'Shellshock'],
];
const AI_CAT_LABELS = new Map(AI_CATEGORIES);

const catLabel = (slug: string) =>
  AI_CAT_LABELS.get(slug) ?? slug.replace(/[_-]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const sevTone = (s: string) => {
  const v = s.toLowerCase();
  return v === 'critical' || v === 'high' ? 'danger' : v === 'medium' ? 'warning' : v === 'low' ? 'info' : 'neutral';
};

const PAGE = 20;

export function AiRulesetTab({ domainId, ai, setAi }: { domainId: string; ai: AiCfg; setAi: <K extends keyof AiCfg>(k: K, v: AiCfg[K]) => void }) {
  const { data: catalog = [], isLoading } = useWafAiCatalog(domainId);
  const [search, setSearch] = useState('');
  const [cat, setCat] = useState('all');
  const [sev, setSev] = useState('all');
  const [page, setPage] = useState(1);

  const counts = useMemo(() => {
    const m = new Map<string, number>();
    catalog.forEach((r) => m.set(r.category, (m.get(r.category) ?? 0) + 1));
    return m;
  }, [catalog]);

  // Categories present in the live catalog — curated order first, then any
  // new categories introduced by the API (alpha sorted).
  const categories = useMemo<[string, string][]>(() => {
    const known = AI_CATEGORIES.filter(([slug]) => counts.has(slug));
    const extra = [...counts.keys()]
      .filter((slug) => !AI_CAT_LABELS.has(slug))
      .sort()
      .map((slug) => [slug, catLabel(slug)] as [string, string]);
    return [...known, ...extra];
  }, [counts]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return catalog.filter((r) => {
      if (cat !== 'all' && r.category !== cat) return false;
      if (sev !== 'all' && r.severity.toLowerCase() !== sev) return false;
      if (q && !(`${r.rule_id} ${r.name}`.toLowerCase().includes(q))) return false;
      return true;
    });
  }, [catalog, search, cat, sev]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE));
  const pageRows = filtered.slice((page - 1) * PAGE, page * PAGE);

  const ruleState = (r: WafAiCatalogRule): 'enabled' | 'monitor' | 'disabled' | 'cat' => {
    if (ai.disabled_categories.includes(r.category)) return 'cat';
    if (ai.disabled_rules.includes(r.rule_id)) return 'disabled';
    if (ai.monitor_rules.includes(r.rule_id)) return 'monitor';
    return 'enabled';
  };

  const setRuleState = (r: WafAiCatalogRule, s: string) => {
    setAi('disabled_rules', ai.disabled_rules.filter((x) => x !== r.rule_id).concat(s === 'disabled' ? [r.rule_id] : []));
    setAi('monitor_rules', ai.monitor_rules.filter((x) => x !== r.rule_id).concat(s === 'monitor' ? [r.rule_id] : []));
  };

  const toggleCategory = (slug: string, on: boolean) =>
    setAi('disabled_categories', on ? ai.disabled_categories.filter((x) => x !== slug) : [...ai.disabled_categories, slug]);

  const columns = useMemo<ColumnDef<WafAiCatalogRule, unknown>[]>(() => [
    { id: 'rule_id', header: 'ID', cell: ({ row }) => <code style={{ fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-muted)' }}>{row.original.rule_id}</code> },
    { id: 'name', header: 'Name', cell: ({ row }) => <span style={{ fontSize: 'var(--vn-font-size-md)' }}>{row.original.name}</span> },
    { id: 'category', header: 'Category', cell: ({ row }) => <span style={{ fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)' }}>{row.original.category}</span> },
    { id: 'severity', header: 'Severity', cell: ({ row }) => <Badge tone={sevTone(row.original.severity)} appearance="subtle" size="sm">{row.original.severity}</Badge> },
    {
      id: 'status', header: 'Status',
      cell: ({ row }) => {
        const st = ruleState(row.original);
        if (st === 'cat') return <Badge tone="neutral" appearance="subtle" size="sm">Disabled (cat.)</Badge>;
        return (
          <SegmentedControl
            size="sm"
            value={st}
            onValueChange={(v) => setRuleState(row.original, v)}
            items={[{ value: 'enabled', label: 'On' }, { value: 'monitor', label: 'Monitor' }, { value: 'disabled', label: 'Off' }]}
          />
        );
      },
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [ai.disabled_categories, ai.disabled_rules, ai.monitor_rules]);

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      <Card>
        <CardHeader
          actions={
            <SettingRow title="Ruleset mode" control={
              <Select value={ai.ruleset_mode} onValueChange={(v) => setAi('ruleset_mode', v)}>
                <SelectTrigger style={{ width: 150 }}><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="detect">Detect only</SelectItem><SelectItem value="block">Block</SelectItem></SelectContent>
              </Select>
            } />
          }
        >
          <CardTitle>AI ruleset</CardTitle><CardDescription>{catalog.length.toLocaleString()} ML-scored rules across {categories.length} categories.</CardDescription>
        </CardHeader>
        <CardBody style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
          <StatGroup columns={3}>
            <Stat label="Total rules" value={catalog.length.toLocaleString()} />
            <Stat label="Categories" value={categories.length} />
            <Stat label="Disabled categories" value={ai.disabled_categories.length} dotColor={ai.disabled_categories.length ? toneColor('warning') : undefined} />
          </StatGroup>
          <div style={{ display: 'grid', gap: 'var(--vn-space-2)', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))' }}>
            {categories.map(([slug, label]) => {
              const on = !ai.disabled_categories.includes(slug);
              return (
                <div key={slug} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--vn-space-2)', border: '1px solid var(--vn-border-default)', borderRadius: 'var(--vn-radius-md)', padding: 'var(--vn-space-2) var(--vn-space-3)', opacity: on ? 1 : 0.6 }}>
                  <span style={{ minWidth: 0 }}>
                    <span style={{ fontSize: 'var(--vn-font-size-sm)', fontWeight: 'var(--vn-font-weight-medium)', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
                    <span style={{ fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-muted)' }}>{counts.get(slug) ?? 0} rules</span>
                  </span>
                  <Switch size="sm" checked={on} onCheckedChange={(v) => toggleCategory(slug, v)} aria-label={label} />
                </div>
              );
            })}
          </div>
        </CardBody>
      </Card>

      <Toolbar
        start={<Input placeholder="Search by ID or name…" value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} style={{ maxWidth: 280 }} />}
        end={
          <>
            <Select value={cat} onValueChange={(v) => { setCat(v); setPage(1); }}>
              <SelectTrigger style={{ width: 160 }}><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">All categories</SelectItem>{categories.map(([s, l]) => <SelectItem key={s} value={s}>{l}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={sev} onValueChange={(v) => { setSev(v); setPage(1); }}>
              <SelectTrigger style={{ width: 140 }}><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">All severities</SelectItem><SelectItem value="critical">Critical</SelectItem><SelectItem value="high">High</SelectItem><SelectItem value="medium">Medium</SelectItem><SelectItem value="low">Low</SelectItem></SelectContent>
            </Select>
          </>
        }
      />

      <Card flat>
        <CardBody style={{ padding: 0 }}>
          {isLoading ? <div style={{ padding: 'var(--vn-space-4)' }}><Skeleton style={{ height: 160 }} /></div> : filtered.length === 0 ? (
            <div style={{ padding: 'var(--vn-space-4)' }}><EmptyState size="sm" icon={<Search size={20} />} title="No rules" description="No catalog rules match the current filters." /></div>
          ) : (
            <DataTable
              columns={columns}
              data={pageRows}
              getRowId={(r) => String(r.id)}
              footer={<Pagination page={page} pageCount={pageCount} onPageChange={setPage} summary={`${filtered.length.toLocaleString()} rules`} />}
            />
          )}
        </CardBody>
      </Card>
    </div>
  );
}
